import React from 'react';
import { FormattedMessage, intlShape } from '../../util/reactIntl';
import { formatMoney } from '../../util/currency';
import { LINE_ITEM_CUSTOMER_COMMISSION, LINE_ITEM_PROVIDER_COMMISSION } from '../../util/types';
import css from './OrderBreakdown.module.css';
import PropTypes from 'prop-types';
const { types } = require('sharetribe-flex-sdk');
const { Money } = types;

/**
 * Calculate the total from line items, including commission line items.
 * Commission line items have negative values for providers, so they subtract correctly.
 * The lineItems passed here are already filtered by user role in OrderBreakdown.
 */
const calculateTotalFromLineItems = (lineItems, currency) => {
  if (!lineItems || lineItems.length === 0) {
    return null;
  }

  // Include all line items (including commissions) but exclude reversals
  const total = lineItems.reduce((sum, item) => {
    if (item.reversal) {
      return sum;
    }
    return sum + (item.lineTotal?.amount || 0);
  }, 0);

  return new Money(total, currency);
};

/**
 * A component that renders the total price including all fees as a line item.
 *
 * @component
 * @param {Object} props
 * @param {Array} props.lineItems - The line items from the transaction
 * @param {string} props.currency - The currency code
 * @param {intlShape} props.intl - The intl object
 * @param {string} [props.labelId] - Optional custom label message ID
 * @returns {JSX.Element|null}
 */
const LineItemCustomTotalPrice = props => {
  const { lineItems, currency, intl, labelId = 'OrderBreakdown.customTotal' } = props;

  // Return null if required props are missing
  if (!lineItems || !currency) {
    return null;
  }

  // Calculate total from all line items (including fees)
  const totalMoney = calculateTotalFromLineItems(lineItems, currency);

  // Return null if no valid total
  if (!totalMoney || totalMoney.amount <= 0) {
    return null;
  }

  const totalLabel = <FormattedMessage id={labelId} />;
  const formattedTotalPrice = formatMoney(intl, totalMoney);

  return (
    <>
      <hr className={css.totalDivider} />
      <div className={css.lineItemTotal}>
        <div className={css.totalLabel}>{totalLabel}</div>
        <div className={css.totalPrice}>{formattedTotalPrice}</div>
      </div>
    </>
  );
};

LineItemCustomTotalPrice.propTypes = {
  lineItems: PropTypes.array,
  currency: PropTypes.string,
  intl: intlShape.isRequired,
  labelId: PropTypes.string,
};

export default LineItemCustomTotalPrice;